import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import React, {useEffect, useMemo, useState} from 'react';
import {useFormik} from 'formik';
import {Box, Grid} from '@material-ui/core';
import useStyles from './EditPrescriptionFormStyles';
import {
    MedicationPackDto,
    MedicinalProductDto,
} from '../../../types/prescription';
import { SelectedPrescription } from '../PrescriptionItem/PrescriptionItem';
import CustomButton from '../../common/Button/Button';
import createNewPrescriptionValidationSchema, {
    DOSAGE_MODES,
    DosageFrequencyOption,
    DosageModeOption,
} from '../../../validators/newPrescription';
import {
    getDosageIntervalData,
    mapDosageToFrequencyOption,
} from '../../../utils/prescriptionDosage';
import PrescriptionFormFields from '../PrescriptionFormFields/PrescriptionFormFields';
import usePrescriptionFormShared from '../hooks/usePrescriptionFormShared';

type EditPrescriptionFormProps = {
    initialValues?: SelectedPrescription;
    onSubmit: (prescription: SelectedPrescription) => void;
    onCancel: () => void;
    insuranceInfoComponent?: React.ReactNode;
};

const parseNullableNumber = (value: number | string): number | null =>
    value === '' || value == null ? null : Number(value);

const EditPrescriptionForm = ({
    initialValues,
    onSubmit,
    onCancel,
    insuranceInfoComponent
}: EditPrescriptionFormProps) => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {t: commonT} = useTranslation(i18nNamespaces.COMMON);
    const classes = useStyles();

    const [medicationPacks, setMedicationPacks] = useState<MedicationPackDto[]>([]);

    const validationSchema = useMemo(
        () =>
            createNewPrescriptionValidationSchema({
                commonT,
                t,
                medicationPacks,
            }),
        [commonT, medicationPacks, t],
    );

    const formik = useFormik({
        initialValues: {
            id: null,
            dosageMode: DOSAGE_MODES.SIMPLIFIED as DosageModeOption,
            medicinalProduct: null as MedicinalProductDto,
            medicationPackId: '' as number | string,
            paymentLevel: '0' as string,
            effectiveDate: new Date(),
            quantity: 1,
            treatmentDuration: '' as number | string,
            dosageFrequencyOption: '' as DosageFrequencyOption | '',
            dosageIntervalHours: '' as number | string,
            dosageAmount: '' as number | string,
            dosageUnit: '',
            usageRecommendation: ''
        },
        validationSchema,
        onSubmit: values => {
            if (!values.medicinalProduct || !values.medicationPackId) return;
            const treatmentDuration = parseNullableNumber(values.treatmentDuration);
            const dosageIntervalHours = parseNullableNumber(values.dosageIntervalHours);
            const dosageAmount = parseNullableNumber(values.dosageAmount);
            const dosageUnit = values.dosageUnit?.trim() || null;
            const {dosageInterval, dosageIntervalType} = getDosageIntervalData(
                values.dosageFrequencyOption,
                dosageIntervalHours,
            );

            onSubmit({
                id: values.id,
                medicinalProduct: values.medicinalProduct,
                medicationPack: medicationPacks.find(
                    pack => pack.id === values.medicationPackId,
                ),
                quantity: values.quantity,
                treatmentDuration,
                dosageInterval,
                dosageIntervalType,
                dosageAmount,
                dosageUnit,
                usageRecommendation: values.usageRecommendation?.trim() ?? '',
                effectiveDate: values.effectiveDate.toISOString(),
                reimbursement:
                    values.paymentLevel === '0'
                        ? {paymentLevel: null, paymentLevelDisplayValue: '100%'}
                        : selectedMedicationPack?.medicationPackReimbursements?.find(
                              pack => pack.paymentLevel === values.paymentLevel,
                          ),
            });
            formik.resetForm();
        },
    });

    const {
        selectedMedicationPack,
        medicationPackDosageUnits,
        hasPredefinedDosageUnits,
        isReimbursementDisabled,
        handleChangeMedicationPack,
        handleChangeDosageMode,
    } = usePrescriptionFormShared({
        formik,
        medicationPacks,
        setMedicationPacks,
    });

    useEffect(() => {
        if (!initialValues) return;
        const {dosageFrequencyOption, dosageIntervalHours} = mapDosageToFrequencyOption(
            initialValues.dosageIntervalType,
            initialValues.dosageInterval,
        );
        formik.setValues({
            id: initialValues.id,
            dosageMode:
                initialValues.medicationPack?.isReimbursed ||
                initialValues.treatmentDuration != null ||
                initialValues.dosageInterval != null ||
                initialValues.dosageIntervalType != null ||
                initialValues.dosageAmount != null ||
                Boolean(initialValues.dosageUnit)
                    ? DOSAGE_MODES.EXTENDED
                    : DOSAGE_MODES.SIMPLIFIED,
            medicinalProduct: initialValues.medicinalProduct,
            medicationPackId: initialValues.medicationPack.id,
            paymentLevel:
                initialValues.reimbursement?.paymentLevel || '0',
            effectiveDate: new Date(),
            quantity: initialValues.quantity,
            treatmentDuration: initialValues.treatmentDuration ?? '',
            dosageFrequencyOption,
            dosageIntervalHours,
            dosageAmount: initialValues.dosageAmount ?? '',
            dosageUnit: initialValues.dosageUnit ?? '',
            usageRecommendation: initialValues.usageRecommendation,
        });
    }, [initialValues]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} className={classes.container}>
                <PrescriptionFormFields
                    t={t}
                    formik={formik}
                    medicationPacks={medicationPacks}
                    handleChangeMedicationPack={handleChangeMedicationPack}
                    hasPredefinedDosageUnits={hasPredefinedDosageUnits}
                    medicationPackDosageUnits={medicationPackDosageUnits}
                    isReimbursementDisabled={isReimbursementDisabled}
                    medicationPackReimbursements={selectedMedicationPack?.medicationPackReimbursements}
                    insuranceInfoComponent={insuranceInfoComponent}
                    onChangeDosageMode={handleChangeDosageMode}
                />
                <Grid item xs={12}>
                    <Box className={classes.buttonRow}>
                        <CustomButton
                            type="button"
                            variant="outlined"
                            color="secondary"
                            className={classes.button}
                            fullWidth
                            onClick={onCancel}
                        >
                            {commonT('cancel')}
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            fullWidth
                        >
                            {t('save')}
                        </CustomButton>
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
};

export default EditPrescriptionForm;
